/**
 * `AddTaskDialog` — create a Pendência. Title is required; detail, priority,
 * due date and case refs are optional. When opened from a case view the case
 * ref is pre-filled (`initialCaseId`) and shown as fixed context.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";
import { addTask, type TaskPriority } from "@/api/lx-tasklist";

const PRIORITIES: TaskPriority[] = ["urgent", "high", "normal", "low"];

export function AddTaskDialog({
  initialCaseId,
  onClose,
  onCreated,
}: {
  initialCaseId?: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [priority, setPriority] = useState<TaskPriority>("normal");
  const [dueDate, setDueDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (title.trim() === "" || busy) return;
    setBusy(true);
    setError(null);
    try {
      await addTask({
        title: title.trim(),
        detail: detail.trim() || undefined,
        priority,
        dueDate: dueDate || undefined,
        caseRefs: initialCaseId ? [initialCaseId] : undefined,
      });
      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="add-task-dialog"
    >
      <div className="w-full max-w-md rounded-lg border border-cream-200 bg-white p-4 shadow-xl dark:border-coffee-800 dark:bg-coffee-950">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            {t("tasklist.addTitle")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.cancel")}
            className="rounded p-1 text-slate-400 hover:bg-cream-100 dark:hover:bg-coffee-800"
          >
            <X size={14} />
          </button>
        </div>

        <label className="mt-3 block text-[11px] text-slate-500 dark:text-slate-400">
          {t("tasklist.fieldTitle")}
        </label>
        <input
          data-testid="add-task-title"
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
          className="mt-1 w-full rounded border border-cream-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-blue-400 dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
        />

        <label className="mt-3 block text-[11px] text-slate-500 dark:text-slate-400">
          {t("tasklist.fieldDetail")}
        </label>
        <textarea
          data-testid="add-task-detail"
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          rows={2}
          className="mt-1 w-full resize-none rounded border border-cream-300 bg-white px-2 py-1.5 text-[13px] outline-none focus:border-blue-400 dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
        />

        <div className="mt-3 flex gap-3">
          <div className="flex-1">
            <label className="block text-[11px] text-slate-500 dark:text-slate-400">
              {t("tasklist.fieldPriority")}
            </label>
            <select
              data-testid="add-task-priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TaskPriority)}
              className="mt-1 w-full rounded border border-cream-300 bg-white px-2 py-1.5 text-[13px] dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(`tasklist.priority.${p}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1">
            <label className="block text-[11px] text-slate-500 dark:text-slate-400">
              {t("tasklist.fieldDue")}
            </label>
            <input
              data-testid="add-task-due"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 w-full rounded border border-cream-300 bg-white px-2 py-1.5 text-[13px] dark:border-coffee-700 dark:bg-coffee-900 dark:text-slate-100"
            />
          </div>
        </div>

        {initialCaseId && (
          <p className="mt-2 text-[11px] text-slate-400 dark:text-slate-500">
            {t("tasklist.addToCaseNote")}
          </p>
        )}

        {error && (
          <div
            data-testid="add-task-error"
            role="alert"
            className="mt-2 rounded border border-red-200/70 bg-red-50 px-2 py-1 text-[11px] text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-[12px] text-slate-600 hover:bg-cream-100 dark:text-slate-300 dark:hover:bg-coffee-800"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            data-testid="add-task-submit"
            disabled={busy || title.trim() === ""}
            onClick={() => void submit()}
            className={cn(
              "rounded px-3 py-1.5 text-[12px] font-medium text-white",
              busy || title.trim() === ""
                ? "cursor-not-allowed bg-blue-300 dark:bg-blue-900/50"
                : "bg-blue-600 hover:bg-blue-700",
            )}
          >
            {t("tasklist.addSubmit")}
          </button>
        </div>
      </div>
    </div>
  );
}
